/* istanbul ignore file */
import React from 'react';
import MuiStepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Check from '@mui/icons-material/Check';
import { Box, StepIconProps } from '@mui/material';
import { StepOwnProps } from '@mui/material/Step/Step';
import { Button } from '../Button/Button';
import type { SxProps, Theme } from '@mui/material/styles';

const StepIcon: React.FC<StepIconProps> = ({ icon, completed, className }) => (
  <div className={className}>{completed ? <Check /> : icon}</div>
);

enum ORIENTATION {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

type StepProps = {
  label: string;
  buttonLabel?: string;
  description?: React.ReactNode;
  isButtonDisabled?: boolean;
  id?: string;
  props?: StepOwnProps;
};

interface StepperProps {
  activeStep: number;
  steps: StepProps[];
  handleNext?: () => void;
  handleBack?: () => void;
  isContent?: boolean;
  customStepComponent?: React.ElementType<StepIconProps, keyof JSX.IntrinsicElements>;
  orientation?: ORIENTATION;
  className?: string;
  stepLabelClassName?: string;
  alternativeLabel?: boolean;
  connector?: React.ReactElement;
  sx?: SxProps<Theme>;
}

const Stepper = ({
  steps,
  activeStep,
  handleNext,
  handleBack,
  isContent = true,
  orientation = ORIENTATION.VERTICAL,
  customStepComponent,
  className = 'stepper',
  stepLabelClassName = '',
  alternativeLabel = false,
  connector,
  sx,
}: StepperProps) => {
  const isVertical = orientation === ORIENTATION.VERTICAL;

  return steps.length ? (
    <MuiStepper
      alternativeLabel={alternativeLabel}
      activeStep={activeStep}
      orientation={orientation}
      className={className}
      connector={connector}
      sx={[
        {
          ...(isVertical && {
            '& .MuiStepConnector-root': {
              ml: '17px',
              cursor: 'default',
            },
            '& .MuiStepContent-root': {
              ml: '17px',
              borderLeftColor: 'var(--mui-palette-text-secondary)',
              pl: '16px',
            },
            '& .MuiStepConnector-line': {
              minHeight: '32px',
              borderColor: 'var(--mui-palette-text-secondary)',
            },
          }),
          ...(!isVertical && {
            '& .MuiStepConnector-root': {
              cursor: 'default',
            },
            '& .MuiStepConnector-line': {
              // fine‑tune horizontal connector position
              ml: '-12px',
              mt: '4px',
              mr: '6px',
            },
          }),
          '& .MuiStepLabel-root': {
            p: 0,
          },
          '& .MuiStepLabel-label': {
            fontWeight: 400,
            fontSize: '15px',
          },
          '& .MuiStepLabel-iconContainer': {
            width: '35px',
            height: '35px',
            background: 'var(--mui-palette-background-primary)',
            p: 0,
            borderRadius: '100px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mr: '20px',
            fontSize: '12px',
            fontWeight: 600,
            '& svg': {
              fontSize: '18px',
              transform: 'translateY(2px)',
            },
            '&.Mui-disabled': {
              backgroundColor: 'action.disabled',
              '& > div': {
                opacity: 0.32,
              },
            },
          },
          '& .MuiSvgIcon-root': {
            fill: 'var(--mui-palette-text-primary)',
          },
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      {steps.map(step => (
        <Step key={step.id || step.label} {...step.props}>
          <StepLabel className={stepLabelClassName} StepIconComponent={customStepComponent || StepIcon}>
            {step.label}
          </StepLabel>
          {isContent ? (
            <StepContent>
              {step.description}
              <Box
                sx={{
                  mt: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  pl: '4px',
                }}
              >
                <Button
                  onClick={handleNext}
                  className="stepper-next-button"
                  data-testid="stepper-next-button"
                  disabled={step.isButtonDisabled}
                  sx={{
                    mr: '8px',
                    fontSize: '12px',
                    '&.Mui-disabled': {
                      cursor: 'not-allowed',
                      pointerEvents: 'initial',
                    },
                  }}
                >
                  {step.buttonLabel}
                </Button>
                {activeStep ? (
                  <Button
                    link
                    onClick={handleBack}
                    className="stepper-back-button"
                    data-testid="stepper-back-button"
                    sx={{
                      fontSize: '12px',
                      p: 0,
                      minWidth: 'auto',
                    }}
                  >
                    Back
                  </Button>
                ) : null}
              </Box>
            </StepContent>
          ) : (
            <></>
          )}
        </Step>
      ))}
    </MuiStepper>
  ) : null;
};

export default Stepper;
